import { useRef, useState } from "react";
import { Camera, X, Plus } from "@phosphor-icons/react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

export const MultiPhotoUpload = ({ photos, onChange, min = 0, max = 5, label, hint, accentClass = "text-slate-600", testid = "multi-photo-upload" }) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const handleChange = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, max - photos.length);
    if (!files.length) return;
    setLoading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        form.append("category", "sattlerei");
        const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      }
      onChange([...photos, ...uploaded]);
      toast.success(`${uploaded.length} Foto${uploaded.length > 1 ? "s" : ""} hochgeladen`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between">
        <label className="block text-sm font-semibold text-slate-700">
          {label} {min > 0 && <span className="text-red-500">*</span>}
        </label>
        <span className={`text-xs font-semibold ${photos.length >= min ? "text-emerald-600" : "text-slate-400"}`} data-testid={`${testid}-count`}>
          {photos.length}/{max}{min > 0 ? ` (min. ${min})` : ""}
        </span>
      </div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2.5">
        {photos.map((p) => (
          <div key={p.file_id} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
            <img src={fileUrl(p.storage_path)} alt={p.original_filename} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(photos.filter((x) => x.file_id !== p.file_id))}
              aria-label="Foto entfernen"
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
              data-testid={`${testid}-remove-${p.file_id}`}
            >
              <X size={13} weight="bold" />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={loading}
            className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-slate-400 bg-white flex flex-col items-center justify-center gap-1 text-slate-500 transition-colors"
            data-testid={`${testid}-add`}
          >
            {loading ? (
              <span className="text-xs">Lade…</span>
            ) : (
              <>
                <span className={`flex ${accentClass}`}><Camera size={22} /><Plus size={13} weight="bold" /></span>
                <span className="text-[11px] font-medium">Foto</span>
              </>
            )}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple onChange={handleChange} className="hidden" />
    </div>
  );
};

export default MultiPhotoUpload;
